import { RedwoodUser } from 'src/auth'
import { RoleType } from 'src/graphql/types/employees'
import { PERMISSION_KEYS } from 'src/lib/permissions'

/**
 * Check if the current user has admin-level access.
 * This includes both ADMIN and OPS roles, as OPS users have the same
 * UI permissions as ADMIN (except for specific restrictions like SSN viewing and CSV downloads).
 *
 * @param currentUser - The current authenticated user
 * @returns true if user is ADMIN or OPS
 */
export const isAdminOrOps = (currentUser: RedwoodUser | null): boolean => {
  if (!currentUser) return false

  // Check new auth structure (active.baseRole)
  const baseRole = currentUser.active?.baseRole
  if (baseRole === 'ADMIN' || baseRole === 'OPS') {
    return true
  }

  // Fallback to old roles array for backward compatibility
  return currentUser.roles?.includes(RoleType.ADMIN) || false
}

/**
 * Check if user is specifically an ADMIN (not OPS).
 * Use this only when you need to distinguish ADMIN from OPS.
 *
 * @param currentUser - The current authenticated user
 * @returns true if user is ADMIN only
 */
export const isAdmin = (currentUser: RedwoodUser | null): boolean => {
  if (!currentUser) return false

  // Check new auth structure
  if (currentUser.active?.baseRole === 'ADMIN') {
    return true
  }

  // Fallback to old roles array
  return currentUser.roles?.includes(RoleType.ADMIN) || false
}

/**
 * Check if user is specifically an OPS role.
 *
 * @param currentUser - The current authenticated user
 * @returns true if user is OPS
 */
export const isOps = (currentUser: RedwoodUser | null): boolean => {
  if (!currentUser) return false

  return currentUser.active?.baseRole === 'OPS'
}

/**
 * Check if the current user has a specific permission key.
 *
 * @param currentUser - The current authenticated user
 * @param permissionKey - The permission key to check
 * @returns true if user has the permission
 */
export const hasPermission = (
  currentUser: RedwoodUser | null,
  permissionKey: string
): boolean => {
  if (!currentUser) return false

  const permissions = currentUser.active?.permissions
  return Array.isArray(permissions) && permissions.includes(permissionKey)
}

/**
 * Whether this user may access marketing spend — budgets and the cost per lead
 * / cost per hire on the Analytics Marketing tab.
 *
 * Gates viewing on its own; editing a budget needs this AND an ADMIN/OPS role
 * (see MarketingBudgetDrawer), which is why the key is `manage:` not `read:`.
 *
 * The grant ALONE — no role fallback, unlike every other check in this file.
 * Clients hand portal logins to their outside marketing agencies, and those
 * users are ordinary employees holding ADMIN / RECRUITER / OPS, so any role
 * fallback would show the budgets to the people the gate exists to hide them
 * from. Mirrors `canAccessMarketingCosts()` in api/src/lib/marketingCostAccess.ts.
 *
 * This is a rendering convenience, NOT the security boundary. `currentUser` is
 * fetched once per page load and never refreshed in-session, so a revoked user
 * keeps the UI until they reload. The server gates independently and is the
 * thing that actually withholds the numbers.
 */
export const canAccessMarketingCosts = (
  currentUser: RedwoodUser | null
): boolean => hasPermission(currentUser, PERMISSION_KEYS.MANAGE_MARKETING_COSTS)
